import liff, { type Liff } from '@line/liff';
import type { AuthSessionResponse, LineAuthRequest } from '@arrows/shared';
import { AuthRequestCoordinator, type AuthClient, type Fetcher } from './auth';

function isAuthSessionResponse(value: unknown): value is AuthSessionResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<AuthSessionResponse>;
  return typeof candidate.sessionToken === 'string'
    && candidate.tokenType === 'Bearer'
    && typeof candidate.expiresIn === 'number';
}

export class LiffAuthClient implements AuthClient {
  private initialization: Promise<boolean> | null = null;
  private session: { readonly token: string; readonly expiresAt: number } | null = null;
  private readonly requests = new AuthRequestCoordinator<string | null>();

  constructor(
    private readonly apiBase: string,
    private readonly liffId: string,
    private readonly fetcher: Fetcher = (input, init) => fetch(input, init),
    private readonly now: () => number = Date.now,
    private readonly sdk: Liff = liff,
  ) {}

  invalidate(): void {
    // LIFF の初期化状態は維持し、期限切れセッションだけを再発行できるようにする。
    this.session = null;
  }

  getSessionToken(forceRefresh = false): Promise<string | null> {
    if (!forceRefresh && this.session !== null && this.session.expiresAt > this.now() + 30_000) {
      return Promise.resolve(this.session.token);
    }
    return this.requests.run(() => this.refreshSession());
  }

  private async refreshSession(): Promise<string | null> {
    try {
      if (!await this.ensureInitialized()) return null;
      // 外部ブラウザでは liff.login() を呼ばず、ゲームを匿名のまま継続する。
      if (!this.sdk.isInClient() || !this.sdk.isLoggedIn()) return null;

      const response = await this.exchangeCurrentIdToken();
      if (response === null) return null;
      const parsed: unknown = await response.json();
      if (!isAuthSessionResponse(parsed)) return null;
      this.session = {
        token: parsed.sessionToken,
        expiresAt: this.now() + parsed.expiresIn * 1_000,
      };
      return this.session.token;
    } catch {
      // LIFF・通信・レスポンス解析の失敗はいずれも同期だけを無効化する。
      return null;
    }
  }

  private ensureInitialized(): Promise<boolean> {
    this.initialization ??= this.sdk.init({ liffId: this.liffId })
      .then(() => true)
      .catch(() => false);
    return this.initialization;
  }

  private async exchangeCurrentIdToken(): Promise<Response | null> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      // liff.init() が取得・更新した生の ID トークンだけをサーバーへ送る。
      const idToken = this.sdk.getIDToken();
      if (idToken === null || idToken.length === 0) return null;
      const body: LineAuthRequest = { idToken };
      const response = await this.fetcher(`${this.apiBase}/v1/auth/line`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.status === 401 && attempt === 0) continue;
      return response.ok ? response : null;
    }
    return null;
  }
}
